import { generatePath } from 'react-router';
import type { Security_AuthenticationProviderOAuthInfo } from 'invApi';
import { AUTHENTICATION_ROUTES } from '../constants';
import { AuthenticationStorage } from './authenticationStorage';

/**
 * OAuth provider metadata from Security/RetrieveAuthenticationProvider with the
 * four values the authorization code flow requires. Providers missing any of
 * them are not usable for OAuth sign in.
 */
export type ConfiguredOAuthProvider =
  Security_AuthenticationProviderOAuthInfo & {
    ClientId: string;
    Id: string;
    Issuer: string;
    SystemId: string;
  };

export function isConfiguredOAuthProvider(
  oAuth?: Security_AuthenticationProviderOAuthInfo | null
): oAuth is ConfiguredOAuthProvider {
  return Boolean(oAuth?.Issuer && oAuth.ClientId && oAuth.SystemId && oAuth.Id);
}

/**
 * Callback URL for a provider, carrying the provider id as a query parameter.
 * The CloudFront response handler and the local dev proxy read that parameter
 * to add the provider's domains to the Content-Security-Policy of the callback
 * response, and the same URL is registered with the provider as its redirect
 * URI.
 */
export function buildOAuthCallbackUrl(providerId: string): string {
  const search = new URLSearchParams({ providerId }).toString();

  return `${WEBPACK_BASENAME.replace(/\/$/, '')}${generatePath(
    AUTHENTICATION_ROUTES.OAUTH_CALLBACK
  )}?${search}`;
}

/**
 * Stores the provider settings for the OAuth callback page and returns its URL.
 * The caller navigates with a full page load so the response carries the
 * provider's Content-Security-Policy.
 */
export function prepareOAuthSignIn(oAuth: ConfiguredOAuthProvider): string {
  AuthenticationStorage.setOAuthSettings({
    authority: oAuth.Issuer,
    clientId: oAuth.ClientId,
    systemId: oAuth.SystemId,
    accessTokenPrefix: oAuth.AccessTokenPrefix,
    oidcDiscoveryUrl: oAuth.OidcDiscoveryUrl
  });

  return buildOAuthCallbackUrl(oAuth.Id);
}
